import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BedDouble, Bath, Maximize, Search, ChevronLeft, ChevronRight, X, Star } from "lucide-react";
import { api, fmtPrice, fmtBaths } from "../lib/api";
import Nav from "../components/site/Nav";
import Footer from "../components/site/Footer";
import FavoriteButton from "../components/site/FavoriteButton";

const PAGE_SIZE = 24;
const selectClass = "bg-white border border-[#0A1128]/20 px-3 py-2.5 text-sm text-[#0A1128] focus:border-[var(--gold)] focus:outline-none w-full";

function Card({ p }) {
  return (
    <Link to={`/property/${p.id}`} data-testid={`all-listing-card-${p.id}`} className="group block bg-white border border-[#0A1128]/10 hover:border-[var(--gold)]/60 transition-colors duration-300">
      <div className="hover-img relative overflow-hidden">
        {p.images?.[0] && <img src={p.images[0]} alt={p.title} loading="lazy" className="w-full h-60 object-cover transition-transform duration-700 group-hover:scale-105" />}
        <div className="absolute top-3 left-3 right-14 flex flex-wrap gap-2">
          <span className="bg-[#FAF9F6]/95 text-[#0A1128] text-[10px] uppercase tracking-[0.16em] px-3 py-1.5">{p.status}</span>
          {(p.feed_status === "New" || p.feed_status === "Reduced") && (
            <span className="bg-[var(--gold)] text-[#0A1128] text-[10px] uppercase tracking-[0.16em] px-3 py-1.5">{p.feed_status}</span>
          )}
          {p.highlighted && (
            <span className="bg-[#0A1128] text-[var(--gold)] text-[10px] uppercase tracking-[0.16em] px-3 py-1.5 flex items-center gap-1"><Star size={10} fill="currentColor" /> Highlighted</span>
          )}
        </div>
        <FavoriteButton id={p.id} className="absolute top-3 right-3" />
      </div>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-serif text-lg text-[#0A1128] leading-snug">{p.title}</h3>
          <div className="font-serif text-lg text-[var(--gold)] whitespace-nowrap">{fmtPrice(p.price, p.currency)}</div>
        </div>
        <p className="text-[var(--slate)] text-xs mt-1">{p.location}{p.mls ? ` · MLS# ${p.mls}` : ""}</p>
        <div className="flex gap-4 mt-3 text-[var(--slate)] text-xs">
          <span className="flex items-center gap-1.5"><BedDouble size={14} /> {p.beds} Beds</span>
          <span className="flex items-center gap-1.5"><Bath size={14} /> {fmtBaths(p.baths)} Baths</span>
          {p.area > 0 && <span className="flex items-center gap-1.5"><Maximize size={14} /> {p.area?.toLocaleString()} sqft</span>}
        </div>
      </div>
    </Link>
  );
}

export default function AllListings() {
  const [profile, setProfile] = useState(null);
  const [all, setAll] = useState(null);
  const [locations, setLocations] = useState([]);
  const [filters, setFilters] = useState({ q: "", location: "", beds: "", min_price: "", max_price: "", sort: "featured", highlighted: false });
  const [page, setPage] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/profile").then((r) => setProfile(r.data)).catch(() => {});
    api.get("/properties").then((r) => setAll(r.data.filter((p) => (p.status || "").toLowerCase() !== "sold"))).catch(() => setAll([]));
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
  }, []);

  const list = useMemo(() => {
    if (!all) return [];
    let out = [...all];
    const q = filters.q.trim().toLowerCase();
    if (q) out = out.filter((p) => [p.title, p.location, p.address].some((s) => (s || "").toLowerCase().includes(q)));
    if (filters.location) out = out.filter((p) => p.location === filters.location);
    if (filters.beds) out = out.filter((p) => p.beds >= Number(filters.beds));
    if (filters.min_price) out = out.filter((p) => p.price >= Number(filters.min_price));
    if (filters.max_price) out = out.filter((p) => p.price <= Number(filters.max_price));
    if (filters.highlighted) out = out.filter((p) => p.highlighted);
    if (filters.sort === "price-asc") out.sort((a, b) => a.price - b.price);
    else if (filters.sort === "price-desc") out.sort((a, b) => b.price - a.price);
    else if (filters.sort === "beds") out.sort((a, b) => b.beds - a.beds);
    return out;
  }, [all, filters]);

  const pages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const slice = list.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const set = (k) => (e) => { setFilters({ ...filters, [k]: e.target.value }); setPage(1); };
  const clear = () => { setFilters({ q: "", location: "", beds: "", min_price: "", max_price: "", sort: "featured", highlighted: false }); setPage(1); };
  const active = filters.q || filters.location || filters.beds || filters.min_price || filters.max_price || filters.highlighted;

  return (
    <div className="bg-[#FAF9F6] min-h-screen" data-testid="all-listings-page">
      <Nav name={profile?.name} logo={profile?.logo} />

      <section className="pt-36 pb-10 px-5 md:px-8 max-w-7xl mx-auto">
        <p className="text-xs uppercase tracking-[0.3em] text-[var(--gold)] mb-4">RE/MAX Cayman Islands</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#0A1128]">All Listings</h1>
        <p className="text-base text-[var(--slate)] mt-5 max-w-2xl leading-relaxed">
          Browse every current RE/MAX Cayman Islands listing — updated daily. Interested in one?
          Contact Mabel for details, viewings, and local insight.
        </p>
      </section>

      <section className="px-5 md:px-8 pb-24 max-w-7xl mx-auto">
        <div data-testid="all-filter-bar" className="bg-white border border-[#0A1128]/12 p-5 md:p-6 grid grid-cols-2 md:grid-cols-6 gap-4 items-end mb-10">
          <div className="col-span-2">
            <label className="text-xs uppercase tracking-[0.16em] text-[var(--slate)]">Search</label>
            <div className="flex items-center gap-2 border border-[#0A1128]/20 bg-white px-3 focus-within:border-[var(--gold)]">
              <Search size={15} className="text-[#0A1128]/50" />
              <input data-testid="all-filter-search" value={filters.q} onChange={set("q")} placeholder="Title, area, address…" className="bg-transparent py-2.5 text-sm w-full focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.16em] text-[var(--slate)]">Area</label>
            <select data-testid="all-filter-location" value={filters.location} onChange={set("location")} className={selectClass}>
              <option value="">All Areas</option>
              {locations.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.16em] text-[var(--slate)]">Beds</label>
            <select data-testid="all-filter-beds" value={filters.beds} onChange={set("beds")} className={selectClass}>
              <option value="">Any</option>
              {[1, 2, 3, 4, 5].map((b) => <option key={b} value={b}>{b}+</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.16em] text-[var(--slate)]">Max Price</label>
            <select data-testid="all-filter-max" value={filters.max_price} onChange={set("max_price")} className={selectClass}>
              <option value="">Any</option>
              {[500000, 750000, 1000000, 2000000, 3000000, 5000000].map((v) => <option key={v} value={v}>{v >= 1000000 ? `$${v / 1000000}M` : `$${v / 1000}K`}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.16em] text-[var(--slate)]">Sort</label>
            <select data-testid="all-filter-sort" value={filters.sort} onChange={set("sort")} className={selectClass}>
              <option value="featured">Featured</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="beds">Most Beds</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.16em] text-[var(--slate)]">&nbsp;</label>
            <button
              type="button"
              data-testid="all-filter-highlighted"
              onClick={() => { setFilters({ ...filters, highlighted: !filters.highlighted }); setPage(1); }}
              className={`w-full flex items-center justify-center gap-2 px-3 py-2.5 text-xs uppercase tracking-[0.14em] border transition-colors duration-300 ${
                filters.highlighted
                  ? "bg-[var(--gold)] border-[var(--gold)] text-[#0A1128]"
                  : "bg-white border-[#0A1128]/25 text-[#0A1128] hover:border-[var(--gold)]"
              }`}
            >
              <Star size={14} fill={filters.highlighted ? "currentColor" : "none"} /> Highlighted Listings
            </button>
          </div>
          {active ? (
            <button data-testid="all-filter-clear" onClick={clear} className="col-span-2 md:col-span-6 flex items-center gap-1 text-xs uppercase tracking-[0.14em] text-[var(--slate)] hover:text-[#0A1128] transition-colors">
              <X size={13} /> Clear filters
            </button>
          ) : null}
        </div>

        {all === null ? (
          <div className="h-40" />
        ) : slice.length === 0 ? (
          <div className="bg-white border border-dashed border-[#0A1128]/15 p-20 text-center text-[var(--slate)]" data-testid="all-no-results">
            No listings match your search. Try adjusting your filters.
          </div>
        ) : (
          <>
            <p className="text-xs uppercase tracking-[0.16em] text-[var(--slate)] mb-6" data-testid="all-results-count">
              {list.length} listing{list.length === 1 ? "" : "s"} · page {current} of {pages}
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {slice.map((p) => <Card key={p.id} p={p} />)}
            </div>
            {pages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-14">
                <button data-testid="all-prev-page" disabled={current === 1} onClick={() => { setPage(current - 1); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  className="flex items-center gap-1 px-5 py-3 border border-[#0A1128]/25 text-sm uppercase tracking-wider disabled:opacity-40 hover:bg-[#E8E4D9]/40 transition-colors">
                  <ChevronLeft size={15} /> Prev
                </button>
                <span className="text-sm text-[var(--slate)]">{current} / {pages}</span>
                <button data-testid="all-next-page" disabled={current === pages} onClick={() => { setPage(current + 1); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  className="flex items-center gap-1 px-5 py-3 border border-[#0A1128]/25 text-sm uppercase tracking-wider disabled:opacity-40 hover:bg-[#E8E4D9]/40 transition-colors">
                  Next <ChevronRight size={15} />
                </button>
              </div>
            )}
          </>
        )}
      </section>

      <Footer profile={profile} />
    </div>
  );
}
